'use client';

import { useOrganizationList, useUser } from '@clerk/nextjs';
import { useState } from 'react';

import { type Plan, planFromMetadata } from '../../contracts/account';
import { track } from '../../libs/observability/analytics.client';
import {
  ATTR_PLAN_SCOPE,
  ATTR_PLAN_VALUE,
  WEB_EVENTS,
} from '../../libs/observability/attributes';

// Free/paid plan controls rendered inside Clerk's account profile modal as
// maintainer-only "Plan (dev)" pages (see AccountMenu). Owner-gated dev tooling —
// the API rejects everyone else — so the strings stay in English rather than
// going through i18n.
const PLANS: readonly Plan[] = ['free', 'paid'];

/** Small page icon for the Clerk custom-page tab label. */
export function PlanIcon(): React.JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M2 3h12v3H2V3zm0 5h12v5H2V8z" />
    </svg>
  );
}

/** The busy set minus one org — kept out of the JSX to stay within callback-nesting limits. */
function withoutOrg(
  current: ReadonlySet<string>,
  orgId: string,
): ReadonlySet<string> {
  return new Set([...current].filter((id) => id !== orgId));
}

async function postPlan(
  scope: 'user' | 'org',
  plan: Plan,
  organizationId?: string,
): Promise<boolean> {
  try {
    const response = await fetch('/api/clerk/plan', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(
        organizationId === undefined
          ? { scope, plan }
          : { scope, plan, organizationId },
      ),
    });
    return response.ok;
  } catch {
    // Best-effort dev toggle: a network failure just leaves the plan unchanged,
    // and the caller always gets a resolved boolean (no unhandled rejection).
    return false;
  }
}

function PlanPanel({
  label,
  current,
  busy,
  onSelect,
}: {
  readonly label: string;
  readonly current: Plan;
  readonly busy: boolean;
  readonly onSelect: (plan: Plan) => void;
}): React.JSX.Element {
  return (
    <div className="text-sm text-gray-900">
      <div className="flex items-center gap-2">
        <span className="w-20 truncate text-gray-700" title={label}>
          {label}
        </span>
        {PLANS.map((plan) => (
          <button
            key={plan}
            type="button"
            aria-label={`${label} ${plan}`}
            aria-pressed={current === plan}
            disabled={busy}
            onClick={() => {
              onSelect(plan);
            }}
            className={`rounded px-3 py-1 capitalize transition-colors ${
              current === plan
                ? 'bg-[#2D1666] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } disabled:opacity-50`}
          >
            {plan}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Personal plan toggle — mounted in the UserButton account profile. */
export function PersonalPlanPanel(): React.JSX.Element | null {
  const { user } = useUser();
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const select = (plan: Plan): void => {
    setBusy(true);
    void postPlan('user', plan)
      .then((ok) => {
        if (!ok) return undefined;
        track(WEB_EVENTS.planChanged, {
          [ATTR_PLAN_SCOPE]: 'user',
          [ATTR_PLAN_VALUE]: plan,
        });
        return user.reload();
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div>
      <h1 className="mb-1 text-base font-semibold text-gray-900">Plan (dev)</h1>
      <p className="mb-3 text-sm text-gray-600">
        Toggle to preview the app in free and paid modes. Only you can see this.
      </p>
      <PlanPanel
        label="Personal"
        // Only the maintainer ever sees this panel, and their default is paid —
        // so fall back to paid until they explicitly toggle to free.
        current={planFromMetadata(user.publicMetadata, 'paid')}
        busy={busy}
        onSelect={select}
      />
    </div>
  );
}

/**
 * Per-org plan toggles — one row per membership, mounted below the personal
 * row in the UserButton "Dev" profile page. Enumerated from the membership
 * list rather than Clerk's ACTIVE organization: the app never sets an active
 * org (there is no OrganizationSwitcher), so an active-org panel could only
 * ever say "select an organization" with no way to select one.
 */
export function OrgPlanPanel(): React.JSX.Element {
  const { isLoaded, userMemberships } = useOrganizationList({
    userMemberships: { pageSize: 50 },
  });
  // A set, not a single id: two org requests can overlap, and the faster one
  // finishing must not re-enable the slower one mid-flight.
  const [busyOrgIds, setBusyOrgIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  if (!isLoaded) return <p className="text-sm text-gray-600">Loading…</p>;
  const memberships = userMemberships.data;
  if (memberships.length === 0) {
    return (
      <p className="text-sm text-gray-600">
        You are not a member of any organization.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {memberships.map(({ organization }) => {
        const select = (plan: Plan): void => {
          setBusyOrgIds((current) => new Set([...current, organization.id]));
          void postPlan('org', plan, organization.id)
            .then((ok) => {
              if (!ok) return undefined;
              track(WEB_EVENTS.planChanged, {
                [ATTR_PLAN_SCOPE]: 'org',
                [ATTR_PLAN_VALUE]: plan,
              });
              return userMemberships.revalidate();
            })
            .finally(() => {
              setBusyOrgIds((current) => withoutOrg(current, organization.id));
            });
        };
        return (
          <PlanPanel
            key={organization.id}
            label={organization.name}
            current={planFromMetadata(organization.publicMetadata)}
            busy={busyOrgIds.has(organization.id)}
            onSelect={select}
          />
        );
      })}
    </div>
  );
}
